/**
 * FileProcessingProgress Component
 *
 * Shows per-file processing status with progress bars when a task iteration
 * is pending (waiting for files) or dispatching (starting Fargate container).
 * Replaces the generic yellow "Waiting to start..." box.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TaskFileProcessingInfo, FileProgressWsMessage } from '@/types/tasks';
import { getFileProcessingStage, getFileProcessingPercent, formatElapsedTime } from '@/types/tasks';
import { useTaskFilesProgress } from '@/hooks';

interface FileProcessingProgressProps {
  taskId: string;
  iterationStatus: 'pending' | 'dispatching';
  className?: string;
  compact?: boolean;
}

export default function FileProcessingProgress({
  taskId,
  iterationStatus,
  className = '',
  compact = false,
}: FileProcessingProgressProps) {
  const { t } = useTranslation('tasks');
  const { data, liveProgress, isLoading, overallPercent, totalEtaSeconds, allReady } = useTaskFilesProgress(
    taskId,
    iterationStatus === 'pending'
  );

  // Dispatching phase
  if (iterationStatus === 'dispatching') {
    return (
      <div className={`rounded-lg border border-blue-200 bg-blue-50/50 overflow-hidden ${className}`}>
        <DispatchPhaseMessages compact={compact} />
      </div>
    );
  }

  // Pending + all ready — brief transition message
  if (allReady && data && data.files.length > 0) {
    return (
      <div className={`rounded-lg border border-green-200 bg-green-50/50 overflow-hidden ${className}`}>
        <div className={`${compact ? 'px-3 py-2' : 'px-4 py-3'} flex items-center gap-2`}>
          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-sm text-green-700">{t('fileProcessing.allReady')}</span>
        </div>
      </div>
    );
  }

  // Pending + no files (or still loading)
  if (isLoading || !data || data.files.length === 0) {
    return (
      <div className={`rounded-lg border border-blue-200 bg-blue-50/50 overflow-hidden ${className}`}>
        <div className={`${compact ? 'px-3 py-2' : 'px-4 py-3'} flex items-center gap-2`}>
          <svg className="w-4 h-4 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm text-blue-700">
            {isLoading ? t('fileProcessing.noFiles') : t('fileProcessing.noFiles')}
          </span>
        </div>
      </div>
    );
  }

  // Pending + files processing — show full panel
  return (
    <div className={`rounded-lg border border-blue-200 bg-blue-50/50 overflow-hidden ${className}`}>
      <FileProgressHeader
        overallPercent={overallPercent}
        totalEtaSeconds={totalEtaSeconds}
        compact={compact}
      />
      <FileList files={data.files} compact={compact} liveProgress={liveProgress} />
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

interface FileProgressHeaderProps {
  overallPercent: number;
  totalEtaSeconds: number;
  compact: boolean;
}

function FileProgressHeader({ overallPercent, totalEtaSeconds, compact }: FileProgressHeaderProps) {
  const { t } = useTranslation('tasks');
  const etaStr = totalEtaSeconds > 0 ? formatElapsedTime(totalEtaSeconds) : '';

  return (
    <div className={`${compact ? 'px-3 py-2' : 'px-4 py-3'} space-y-2`}>
      {/* Progress bar row */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-blue-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${overallPercent}%` }}
          />
        </div>
        <span className="text-xs font-medium text-blue-700 whitespace-nowrap w-10 text-right">
          {overallPercent}%
        </span>
        {etaStr && (
          <span className="text-xs text-blue-600 whitespace-nowrap">
            {t('fileProcessing.eta', { time: etaStr })}
          </span>
        )}
      </div>
      <div className="text-sm text-blue-700">{t('fileProcessing.preparing')}</div>
    </div>
  );
}

interface FileListProps {
  files: TaskFileProcessingInfo[];
  compact: boolean;
  liveProgress: Map<string, FileProgressWsMessage>;
}

function FileList({ files, compact, liveProgress }: FileListProps) {
  const { t } = useTranslation('tasks');

  return (
    <div className={`${compact ? 'px-3 pb-2' : 'px-4 pb-3'} space-y-1`}>
      {files.map((file) => {
        const fileLive = liveProgress.get(file.file_id) ?? null;
        const stage = getFileProcessingStage(file, fileLive);
        const percent = getFileProcessingPercent(file, fileLive);
        const isReady = file.status === 'ready';
        const isFailed = file.status === 'failed';
        const isProcessing = file.status === 'processing';

        // Translate stage key — stage may contain page info like "OCR 31/69"
        const stageBase = stage.split(' ')[0];
        const stageKeyMap: Record<string, string> = {
          'Ready': 'ready',
          'Queued': 'queued',
          'Failed': 'failed',
          'OCR': 'ocr',
          'Scouting': 'scouting',
          'Content': 'contentMining',
          'Page': 'pagePreviews',
          'Indexing': 'indexing',
          'Finalizing': 'finalizing',
        };
        const hasPageInfo = stage.includes('/');
        const pageInfoSuffix = hasPageInfo ? ' ' + stage.split(' ').slice(1).join(' ') : '';
        const stageLabel = t(`fileProcessing.stage.${stageKeyMap[stageBase] ?? 'finalizing'}`) + pageInfoSuffix;

        return (
          <div
            key={file.file_id}
            className={`flex items-center gap-2 text-sm ${compact ? 'py-0.5' : 'py-1'}`}
          >
            {/* Status icon */}
            <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
              {isReady ? (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : isFailed ? (
                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              ) : isProcessing ? (
                <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <span className="w-3 h-3 rounded-full border-2 border-gray-300" />
              )}
            </span>

            {/* File name */}
            <span
              className={`truncate min-w-0 flex-shrink ${
                isReady ? 'line-through opacity-75 text-gray-500' : 'text-gray-700'
              }`}
              title={file.file_name}
            >
              {file.file_name}
              {file.page_count !== null && (
                <span className="text-xs text-gray-400 ml-1">({file.page_count}p)</span>
              )}
            </span>

            {/* Stage label + mini progress */}
            <span className="flex-shrink-0 ml-auto flex items-center gap-2">
              <span className={`text-xs whitespace-nowrap ${
                isReady ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-blue-600'
              }`}>
                {stageLabel}
              </span>
              {isProcessing && (
                <div className="flex items-center gap-1">
                  <div className="w-16 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <span className="text-xs text-blue-500 w-8 text-right">{percent}%</span>
                </div>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface DispatchPhaseMessagesProps {
  compact: boolean;
}

function DispatchPhaseMessages({ compact }: DispatchPhaseMessagesProps) {
  const { t } = useTranslation('tasks');
  const startTimeRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  let messageKey: string;
  if (elapsed < 5) {
    messageKey = 'dispatch.provisioning';
  } else if (elapsed < 15) {
    messageKey = 'dispatch.allocating';
  } else if (elapsed < 30) {
    messageKey = 'dispatch.initializing';
  } else if (elapsed < 45) {
    messageKey = 'dispatch.loading';
  } else {
    messageKey = 'dispatch.connecting';
  }

  return (
    <div className={`${compact ? 'px-3 py-2' : 'px-4 py-3'} flex items-center gap-2`}>
      <svg className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <span className="text-sm text-blue-700">{t(`fileProcessing.${messageKey}`)}</span>
    </div>
  );
}
